// QBR grading — pure functions that turn a metric value into an A/B/C/D
// letter grade against a threshold config. Thresholds live in the
// qbr_grading_thresholds table; the shape matches what migration 025 seeds.
//
// Each metric has its own schema because the question "is this good?" varies
// per metric (cutoffs, composite signals, direction). Adding a new metric =
// add a new case here + a seed row in migration 025.

const GRADES = ['A', 'B', 'C', 'D'];

// `value` shape per metric — these are the values the metrics module returns.
//   completion_rate:      { pct: number }
//   response_time:        { responseDays: number, resolutionDays: number, trend: 'down'|'flat'|'up' }
//   planned_vs_reactive:  { pct: number }
//   pm_compliance:        { actualPct: number, targetPct: number }  // delta = actual - target
//   backlog_aging:        { overdue30dPct: number, oldestDays: number }
//   adoption:             { activePct: number, mobilePct: number }
export function gradeMetric(metric, value, thresholdConfig) {
  if (!thresholdConfig || !value) return null;
  const tiers = thresholdConfig.tiers || [];

  switch (metric) {
    case 'completion_rate':
    case 'planned_vs_reactive':
      return firstMatchingTier(tiers, t => value.pct >= t.min);

    case 'response_time':
      // Both graded medians must exist. A missing series used to flow
      // in as 0 days — the best possible value — and inflate the grade.
      if (value.responseDays == null || value.resolutionDays == null) return null;
      return firstMatchingTier(tiers, t => {
        if (t.response_max == null && t.resolution_max == null) return true;
        const respOk = t.response_max == null || value.responseDays <= t.response_max;
        const resOk  = t.resolution_max == null || value.resolutionDays <= t.resolution_max;
        if (t.requires_trend_down && value.trend !== 'down') return false;
        return respOk && resOk;
      });

    case 'pm_compliance': {
      // No committed target (or no measured actual) → no grade. The old
      // ?? 0 fallbacks turned a missing target into delta = actual − 0:
      // an automatic A regardless of performance.
      if (value.actualPct == null || value.targetPct == null) return null;
      const delta = value.actualPct - value.targetPct;
      return firstMatchingTier(tiers, t => delta >= t.min_delta);
    }

    case 'backlog_aging':
      return firstMatchingTier(tiers, t =>
        value.overdue30dPct <= t.overdue_30d_max_pct &&
        value.oldestDays   <= t.oldest_max_days
      );

    case 'adoption':
    case 'adoption_power':
      // Same rubric as the overall adoption metric — both are
      // active% + mobile% against the same thresholds. The power-user
      // variant just narrows the user population before bucketing.
      // active% is required; mobile% gates a tier only when it's
      // actually known — FMX's API has no mobile-usage signal, so an
      // auto-pull leaves it null, and requiring it forced every
      // auto-pulled adoption grade to D. An explicitly entered 0 still
      // gates.
      if (value.activePct == null) return null;
      return firstMatchingTier(tiers, t =>
        value.activePct >= t.active_min_pct &&
        (value.mobilePct == null || value.mobilePct >= t.mobile_min_pct)
      );

    default:
      return null;
  }
}

function firstMatchingTier(tiers, predicate) {
  const ordered = [...tiers].sort((a, b) => GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade));
  for (const t of ordered) {
    if (predicate(t)) return t.grade;
  }
  return 'D';
}

// Map a grade to the badge color used in the .pptx and preview UI.
// Matches the rubric the user specified: A = green, B = electric blue,
// C = warm amber, D = red. F only occurs as a headline (average) grade,
// never per-metric — darker red than D.
export function gradeColor(grade) {
  switch (grade) {
    case 'A': return '#2E7D32';
    case 'B': return '#1FB6FF';
    case 'C': return '#E08A1F';
    case 'D': return '#B91C1C';
    case 'F': return '#7F1D1D';
    default:  return '#9CA3AF';
  }
}

// Canonical metric list for the QBR (defines slide order in the deck).
// adoption_power shares grading.js's adoption rubric (see gradeMetric
// case 'adoption_power') and shares the schema in metrics.js. Listed
// here so the Data step's compute loop produces a grade for it.
export const QBR_METRICS = [
  { key: 'completion_rate',     label: 'Maintenance Activity',           unit: '%' },
  { key: 'response_time',       label: 'Team Performance',               unit: 'days' },
  { key: 'planned_vs_reactive', label: 'Planned vs Reactive',            unit: '%' },
  { key: 'pm_compliance',       label: 'PM Compliance',                  unit: '%' },
  { key: 'backlog_aging',       label: 'Open Backlog & Aging',           unit: 'composite' },
  { key: 'adoption',            label: 'Adoption & Engagement',          unit: 'composite' },
  { key: 'adoption_power',      label: 'Power-User Adoption & Engagement', unit: 'composite' },
];
